// Message.jsx
import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';

const Message = ({ type, message }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation after component mounts
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <XCircle size={20} />;
      case 'warning':
        return <AlertCircle size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  return (
    <div className={`message-container ${type} ${isVisible ? 'visible' : ''}`}>
      <div className="message-icon">
        {getIcon()}
      </div>
      <div className="message-content">
        {message}
      </div>
      <div className={`message-progress ${type}`}></div>

      <style jsx>{`
        .message-container {
          display: flex;
          align-items: center;
          background: rgba(31, 41, 55, 0.95);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
          border-left: 4px solid transparent;
          max-width: 400px;
          transform: translateY(-20px);
          opacity: 0;
          transition: transform 0.3s ease, opacity 0.3s ease;
          backdrop-filter: blur(4px);
        }
        
        .message-container.visible {
          transform: translateY(0);
          opacity: 1;
        }
        
        .message-icon {
          margin-right: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .message-content {
          color: white;
          font-weight: 500;
          font-size: 15px;
          line-height: 1.5;
        }
        
        .message-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          width: 100%;
          animation: progress 5s linear forwards;
        }
        
        /* Success */
        .message-container.success {
          border-left-color: #10B981;
          background: linear-gradient(90deg, rgba(16, 185, 129, 0.1), rgba(31, 41, 55, 0.95));
        }
        
        .message-container.success .message-icon {
          color: #10B981;
        }
        
        .message-progress.success {
          background-color: #10B981;
        }
        
        /* Error */
        .message-container.error {
          border-left-color: #EF4444;
          background: linear-gradient(90deg, rgba(239, 68, 68, 0.1), rgba(31, 41, 55, 0.95));
        }
        
        .message-container.error .message-icon {
          color: #EF4444;
        }
        
        .message-progress.error {
          background-color: #EF4444;
        }
        
        /* Warning */
        .message-container.warning {
          border-left-color: #F59E0B;
          background: linear-gradient(90deg, rgba(245, 158, 11, 0.1), rgba(31, 41, 55, 0.95));
        }
        
        .message-container.warning .message-icon {
          color: #F59E0B;
        }
        
        .message-progress.warning {
          background-color: #F59E0B;
        }
        
        /* Info */
        .message-container.info {
          border-left-color: #3B82F6;
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.1), rgba(31, 41, 55, 0.95));
        }
        
        .message-container.info .message-icon {
          color: #3B82F6;
        }
        
        .message-progress.info {
          background-color: #3B82F6;
        }
        
        @keyframes progress {
          0% {
            width: 100%;
          }
          100% {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

export default Message;